import dom from "../../../dist/jsx-render/dom"
import { ITableColumn } from "./ITableColumn";
import { TableColumnAlign } from "./TableColumnAlign";
import { TableColumnTypes } from "./TableColumnTypes";
import { ListItem } from "../../Model/ListItem";
import { MsoTable } from "./MsoTable";
import { app } from "../../Frame/App";

export class ColumnBase {
    public index: number;
    public filteredItems: { [id: string]: number };
    protected recreateCellOnUpdate: boolean = false;

    get width(): number {
        return this.def.width;
    }

    set width(value: number) {
        this.def.width = value;
    }

    get defaultWidth(): string {
        return this.def.defaultWidth;
    }

    set defaultWidth(value: string) {
        this.def.defaultWidth = value;
    }

    get headerText(): string {
        return this.def.headerText;
    }

    get headerElement(): HTMLElement {
        return this.def.headerElement;
    }

    set headerText(value: string) {
        this.def.headerText = value;
    }

    get headerCssClass(): string {
        return this.def.headerCssClass;
    }

    get sizerClass(): string {
        return "mt-sizer mt-sizer-" + this.rootId;
    }

    get field(): string {
        return this.def.dataField;
    }

    get number(): number {
        return this.def.number;
    }

    get sortAsc(): boolean {
        return this.def.sortAsc;
    }

    get isSortable(): boolean {
        return this.def.isSortable;
    }

    get type(): TableColumnTypes {
        return this.def.type;
    }

    get decimals(): number {
        return this.def.decimals;
    }

    get fixedWidth(): string {
        return this.def.fixedWidth;
    }

    get isEditable(): boolean {
        return this.def.isEditable;
    }

    get isEditLink(): boolean {
        return this.def.isEditLink;
    }

    get isSizeable(): boolean {
        return this.def.isSizeable;
    }

    get isFilterable(): boolean {
        return this.def.isFilterable;
    }

    get prohibitRowClick(): boolean {
        return this.def.prohibitRowClick;
    }

    get cssClass(): string {
        return this.def.cssClass;
    }

    public constructor(protected table: MsoTable, protected def: ITableColumn, protected rootId: string) {
        if (def.width == 0 && !def.defaultWidth) {
            def.width = 200;
        }

        if (def.isSizeable === undefined) {
            def.isSizeable = true;
        }
        if (def.isSortable === undefined) {
            def.isSortable = true;
        }
        if (def.isFilterable === undefined) {
            def.isFilterable = true;
        }
    }

    public createCell(rowModel: any, idField: string): HTMLElement {
        let cell: HTMLElement;
        
        if (this.table && this.table.props.fixedLayout) {
            cell = <td className={this.getCellClass()}>
                {this.getContent(rowModel)}
            </td>
        }
        else {
            cell = <div className={this.getCellClass()}>
                {this.getContent(rowModel)}
            </div>
        }

        if (this.def.toolTip) {
            cell.title = this.def.toolTip;
        }

        // cell click only on non editable fields
        if (this.def.fireOnCellClick && this.table.props.onCellClick) {
            cell.classList.add("pointer");
            cell.addEventListener("click", (ev: MouseEvent) => {
                this.table.props.onCellClick(this.def.dataField, rowModel[idField]);
                ev.preventDefault();
                ev.stopPropagation();
                ev.cancelBubble = true;
            });
        }

        if (this.def.prohibitRowClick === true || this.def.isEditable) {
            cell.addEventListener("click", (event: MouseEvent) => {
                event.stopPropagation();
            });
        }
        
        cell.style.width = this.calculateWidth();

        if (!this.isEditLink && this.def.htmlEncode == false && this.type != TableColumnTypes.Custom) {
            cell.style.overflowX = "hidden";
        }

        if (this.def.colorField) {
            let color: string = rowModel[this.def.colorField];
            if (color && color.length > 0) {
                cell.style.backgroundColor = color;
            }
        }

        return cell;
    }

    public calculateWidth = (): string => {
        if (this.table && this.defaultWidth && !this.width) {
            // determine if the width has a unit. If it doesn't, append the unit "px"
            let widthNumber = Number(this.defaultWidth);
            if (!isNaN(widthNumber)) {
                return this.defaultWidth + "px";
            }
            else {
                if (this.defaultWidth.indexOf("%") != -1) {
                    return this.calculatePercentWith(this.defaultWidth);
                }
                else {
                    return this.defaultWidth;
                }
            }
        }

        if (this.table && this.table.props.fixedLayout && this.fixedWidth) {
            return this.fixedWidth + "px";
        }

        return null;
    }

    public resetWidth = () => {
        if (this.def.defaultWidth) {
            this.width = undefined;
        } else {
            // default column size
            this.width = 200;
        }
    }

    updateCell(cell: HTMLElement, model: any): HTMLElement {
        if (this.recreateCellOnUpdate) {
            let newCell = this.createCell(model, this.table.props.idField);
            newCell.style.width = cell.style.width;
            cell.parentElement.insertBefore(newCell, cell);
            cell.parentElement.removeChild(cell);
        }
        else {
            cell.innerHTML = "";
            let newContent = this.getContent(model);
            if (newContent) {
                cell.appendChild(newContent);
            }

            if (!this.isEditLink && this.def.htmlEncode == false && this.type != TableColumnTypes.Custom) cell.style.overflowX = "hidden";
        }

        return cell;
    }

    public getContent(row: any): HTMLElement {
        const content = row[this.def.dataField] ?? '\u00A0';
        if (this.isEditLink) {
            const link: HTMLElement = <a className="mt-link" href="#"><span>{content}</span></a>;

            if (this.table.props.onEditIconClicked) {
                link.addEventListener("click", () => {
                    this.table.props.onEditIconClicked(this.field, row);
                });
            }
            return link;
        }
        else {
            return <span>{content}</span>;
        }
    }

    public getCellClass(): string {
        let className = "mt-cell";
        if (this.def.align == TableColumnAlign.Right) {
            className += " text-right justify-content-end";
        }
        else if (this.def.align == TableColumnAlign.Center) {
            className += " text-center justify-content-center";
        }
        if (this.def.cssClass) {
            className += " " + this.def.cssClass;
        }
        return className;
    }

    public compare(modelA: any, modelB: any, asc: boolean): number {
        let aValue: string = modelA[this.field] ? modelA[this.field] : "";
        let bValue: string = modelB[this.field] ? modelB[this.field] : "";
        if (asc) {
            return aValue.localeCompare(bValue);
        }
        else {
            return bValue.localeCompare(aValue);
        }
    }

    public isCellVisible(model: any) {
        if (this.def.visibilityField) {
            return model[this.def.visibilityField];
        }
        return true;
    }

    protected getRowModelsForFilter(table: MsoTable): Array<any> {
        if (this.filteredItems) {
            return table.getAllRowModels();
        }
        else {
            return table.getVisibileRowModels();
        }
    }

    public getUniqueVisibleItems(table: MsoTable): Array<ListItem> {
        let result = new Array<ListItem>();
        let dict: { [id: string]: number } = {};
        let isNull: boolean = false;
        for (let model of this.getRowModelsForFilter(table)) {
            let text = model[this.field];

            if (app.isNullOrUndefined(text)) {
                isNull = true;
            }
            else {
                if (typeof text === "number") {
                    text = text.toString();
                }

                if (text && text.length > 0) {
                    if (!(text in dict)) {
                        dict[text] = 1;
                    }
                }
            }
        }

        for (let text in dict) {
            result.push({ id: text, name: text });
        }

        result.sort((a: ListItem, b: ListItem): number => {
            return a.name.localeCompare(b.name);
        });

        if (isNull)
            result.unshift({ id: "null", name: `${Resources.Common.null}` });

        return result;
    }

    public isFilterChecked(value: string): boolean {
        if (this.filteredItems) {
            return (value in this.filteredItems);
        }
        return true;
    }

    public isFilterVisible(model: any): boolean {
        if (this.filteredItems) {
            if (this.field.startsWith("CAT#") && model[this.field] != null && typeof (model[this.field]) == 'string' && model[this.field].trim() != "") {
                let exist: boolean = false;
                let splitItem = model[this.field].split(",");
                for (let item in splitItem) {
                    if (splitItem[item].trim() in this.filteredItems)
                        exist = true
                }
                return exist;
            }
            else {
                let value = model[this.field];
                if (typeof (value) == 'string' && value.trim() == "")
                    value = null;

                return value in this.filteredItems;
            }
        }
        return true;
    }

    public hideContent(cell: HTMLElement): void {
    }

    /**
     *  This method will determine the visible size of the table's container and calculates a pixel size: (percentage * visual width)
     * @param width A string like '20%'
     * @returns
     */
    public calculatePercentWith = (width: string): string => {
        let numberString = width.replace("%", "");
        let percent = Number(numberString);

        if (!isNaN(percent)) {
            // get width of parent container of the table
            if (this.table.rootElement && this.table.rootElement.parentElement) {
                let parent = this.table.rootElement.parentElement;

                // Client width should return the real width on the screen without the additional with that is scrolled outside of the window.
                let parentWidth = parent.clientWidth;

                let percentageWidth = Math.round(parentWidth * (percent / 100));
                return percentageWidth + "px";
            }
        }

        // What should we do here? Just return the value that was passed to this method.
        return width;
    }
}